import React, { useEffect, useState } from 'react';
import './ManagePlans.scss';
import { Plan } from '../../store/slices/MRSlice';
import { useDispatch, useSelector } from 'react-redux';
import PricingCard from '../PricingCard/PricingCard';
import { setCall } from '../../store/slices/ScheduleACallSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { routes } from '../../utils/routes';
import { billingCheckoutAction, getBillingPlanAction, getConfigurationAction, getUserProfileAction } from '../../redux/actions';
import { AppDispatch, RootState } from '../../store/store';
import { Box, Grid, MenuItem, Select, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { menuItemSX } from '../Builder/BuilderConst';
import { commonSwitchStyles } from '../../theme/CommonStyles';
import { billingupgradePreviewAction } from '../../redux/actions/UserActions/BillingActions';
import UpgradePreviewModal from '../Modals/UpgradePreviewModal';

const planPriority: any = {
  starter_rank_tracker: 1,
  starter_full: 2,
  pro_rank_map: 3
};

const typographyStyle = {
  fontFamily: 'var(--font-family-manrope)',
  fontWeight: 400,
  fontSize: '16px'
};

export default function ManagePlanPrice() {
  const [activePlan, setActive] = useState<string>('starter');

  const membershipData = useSelector((state: RootState) => state.dashboard.dashboardData?.membership);
  const dashboardData = useSelector((state: RootState) => state.dashboard.dashboardData);
  const { getBillingPlanData } = useSelector((state: RootState) => state.billing);
  const { configurationData } = useSelector((state: RootState) => state.helper);
  // const { billingUpgradePreviewData } = useSelector((state: RootState) => state.billing);

  const [generatedGridOptions, setGeneratedGridOptions] = useState<{ key: string; label: string; value: number }[]>([]);
  const [NumberOfScan, setNumberOfScan] = useState<number>(0);
  const [value, setValue] = useState<number>(0);
  const [selectGrid, setSelectGrid] = useState('');
  const [selectTime, setSelectTime] = useState('Monthly');
  const [estimatedCredits, setEstimatedCredits] = useState<number>(0);
  const [recommendedPlanId, setRecommendedPlanId] = useState<string>('');
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const config = configurationData?.[0];

  const [isUpgradePreviewOpen, setIsUpgradePreviewOpen] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location?.state?.tab !== undefined) {
      setValue(location.state.tab);
    }
  }, [location?.state?.tab]);

  useEffect(() => {
    dispatch(getBillingPlanAction());
    dispatch(getConfigurationAction());
  }, []);

  useEffect(() => {
    if (config?.grid_array) {
      const gridArray = config.grid_array || [];
      const costPerPin = Number(config?.per_pin_cost || 0);

      const options = gridArray.map((item: any) => {
        const key = Number(Object.keys(item)[0]);
        const value = key * key * costPerPin;

        return {
          key,
          label: `${key}x${key} grid (${value} Credits)`,
          value
        };
      });
      setGeneratedGridOptions(options);

      const matchedOption = options.find((opt: any) => opt.key === config?.grid?.value);
      setSelectGrid(matchedOption?.label || options[0]?.label || '');
    }
  }, [config]);

  useEffect(() => {
    const selectedOption = generatedGridOptions.find(opt => opt.label === selectGrid);
    const Credits = selectedOption ? selectedOption.value : 0;

    const time = selectTime.toLowerCase();
    let frequency = 1;
    if (time === 'daily') frequency = 30;
    else if (time === 'weekly') frequency = 4;
    else if (time === 'monthly') frequency = 1;

    const total = NumberOfScan * Credits * frequency;
    setEstimatedCredits(total);
  }, [NumberOfScan, selectGrid, selectTime, generatedGridOptions]);

  useEffect(() => {
    if (NumberOfScan === 0 || estimatedCredits === 0) {
      setRecommendedPlanId('');
      return;
    }

    if (!getBillingPlanData || getBillingPlanData.length === 0) return;

    const enabledPlans = getBillingPlanData.filter((p: Plan) => !p.disabled).sort((a: Plan, b: Plan) => a.credits - b.credits);

    if (enabledPlans.length === 0) return;

    // If no estimation yet, maybe default to first or none? User said "when estimated... is near close", implies we have a value.
    // Let's stick to: smallest plan >= estimated.
    // If estimated is 0, it will pick the first one (smallest).
    let bestFit = enabledPlans.find((p: Plan) => p.credits >= estimatedCredits);

    if (!bestFit) {
      // If estimatedCredits is larger than all plans, recommend the largest one
      bestFit = enabledPlans[enabledPlans.length - 1];
    }

    if (bestFit) {
      setRecommendedPlanId(bestFit._id);
    }
  }, [NumberOfScan, estimatedCredits, getBillingPlanData]);

  useEffect(() => {
    if (getBillingPlanData?.length > 0) {
      const firstAvailableCategory = getBillingPlanData
        ?.filter((item: Plan) => !item?.disabled)
        ?.map((item: Plan) => item?.category)
        ?.filter((cat: string, index: number, arr: string[]) => arr?.indexOf(cat) === index)[0];

      if (firstAvailableCategory) {
        setActive(firstAvailableCategory);
      }
    }
  }, [getBillingPlanData]);

  useEffect(() => {
    dispatch(getUserProfileAction());
  }, []);

  const handleBillCheckout = async (data: Plan, label: string) => {
    const payload = {
      planId: data?.planId,
      type: (billing === 'yearly' && data?.yearly === true) ? 'yearly' : 'monthly',
      purchaseType: label === 'Upgrade' ? 'upgrade' : 'purchase'
    };

    try {
      if (activePlanData?.category === 'pro') {
        dispatch(setCall());
        navigate(routes?.scheduleACall);
      } else {
        dispatch(billingCheckoutAction(payload))
          .unwrap()
          .then(result => {
            if (result?.success) {
              window.location.href = result?.data?.url;
            }
          })
          .catch(err => console.error(err));
      }
    } catch (err) {
      console.error('API error:', err);
    }
  };

  const handleScheduleACall = () => {
    dispatch(setCall());
    navigate(routes?.scheduleACall);
  };

  const handleMapCheckinsAtLocation = () => {
    // dispatch(setCall());
    navigate(routes?.mapCheckingDashboard);
  };


  const pricingPlans = [
    // {
    //   id: 'map-ranking-booster',
    //   title: 'Map Ranking Booster',
    //   price: 699,
    //   priceLabel: '$699/mo',
    //   description: 'Increase map pack rankings and sales for your most profitable service.',
    //   ctaLabel: 'Book a Call',
    //   // ctaAction: handleBoosterScheduleCall,
    //   ctaAction: handleScheduleACall,
    //   features: ['GBP entity reinforcement', 'Website entity reinforcement', 'Structured data (schema)', 'CTR & engagement boosters', 'Driving direction signals', 'Map check-ins', 'Heatmap monitoring']
    // },
    // {
    //   id: 'map-pack-dominator',
    //   title: 'Map Pack Dominator',
    //   price: 1397,
    //   priceLabel: '$1,397/mo',
    //   description: 'Dominate Google Maps in the neighborhoods that matter most.',
    //   ctaLabel: 'Book a Call',
    //   ctaAction: handleScheduleACall,
    //   features: ['GBP optimization', 'Citations (NAP)', 'Maps + Website SEO', 'AI-driven optimization', 'Rank tracking', 'Account manager + monthly Zoom']
    // },
    // {
    //   id: 'white-label',
    //   title: 'White Label for Agencies',
    //   price: null,
    //   priceLabel: 'Custom',
    //   description: 'Offer Map Ranking under your brand.',
    //   ctaLabel: 'Book a Call',
    //   // ctaAction: () => console.log("White Label for Agencies"),
    //   ctaAction: handleScheduleACall,
    //   features: ['White-label dashboards', 'Agency pricing', 'Backend fulfillment', 'Dedicated support', 'Scalable delivery']
    // },
    // {
    //   id: 'map-checkins',
    //   title: 'Map Check-Ins',
    //   price: 299,
    //   priceLabel: '$299/mo',
    //   description: 'Best for local service based businesses.',
    //   ctaLabel: 'Add a Location',
    //   ctaAction: handleMapCheckinsAtLocation,
    //   features: ['Map Check-Ins App', 'Unlimited users', 'Unlimited map check-ins', 'Dedicated customer support', 'Software setup and configuration']
    // },
    ...(dashboardData?.addons
      ?.filter((addon: any) => addon.addon_id === 'map_checkin')
      ?.map((addon: any) => ({
        _id: addon._id,
        id: addon.addon_id,
        title: addon.name,
        price: addon.price,
        priceLabel: `$${addon.price}/mo`,
        description: 'Best for local service based businesses.',
        ctaLabel: 'Add a Location',
        ctaAction: handleMapCheckinsAtLocation,
        features: addon.features?.map((f: any) => f.title) || []
      })) || []),
    {
      id: 'white-label',
      title: 'White Label for Agencies',
      price: null,
      priceLabel: 'Custom',
      description: 'Offer Map Ranking under your brand.',
      ctaLabel: 'Book a Call',
      // ctaAction: () => console.log("White Label for Agencies"),
      ctaAction: handleScheduleACall,
      features: ['White-label dashboards', 'Agency pricing', 'Backend fulfillment', 'Dedicated support', 'Scalable delivery']
    },
  ];

  const handleUpgradePreview = async (plan: Plan) => {
    try {
      await dispatch(
        billingupgradePreviewAction({
          planId: plan.planId,
          type: (billing === 'yearly' && plan?.yearly === true) ? 'yearly' : 'monthly'
        })
      ).unwrap();

      setUpgradePlan(plan);
      setIsUpgradePreviewOpen(true);
    } catch (error) {
      console.error('Upgrade preview failed', error);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradePlan) return;

    setLoading(true);

    try {
      const result = await dispatch(
        billingCheckoutAction({
          planId: upgradePlan.planId,
          type: (billing === 'yearly' && upgradePlan?.yearly === true) ? 'yearly' : 'monthly',
          purchaseType: 'upgrade'
        })
      ).unwrap();

      if (result?.success) {
        setIsUpgradePreviewOpen(false);
        dispatch(getUserProfileAction());
      }
    } catch (error) {
      console.error('Checkout failed', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanButtonState = (item: Plan): { label: string; disabled: boolean } => {
    if (item.disabled) {
      return { label: 'Unavailable', disabled: true };
    }

    if (!membershipData || membershipData.status !== 'active') {
      return { label: 'Subscribe', disabled: false };
    }

    const isYearlySupported = item?.yearly === true;
    const targetType: 'monthly' | 'yearly' = (billing === 'yearly' && isYearlySupported) ? 'yearly' : 'monthly';

    const isSamePlan = membershipData?.planId === item?.planId;
    const isSameType = membershipData?.type === targetType;

    // Case 1: Same plan + same billing type
    if (isSamePlan && isSameType) {
      return { label: 'Active', disabled: true };
    }

    // Case 2: Same plan + different billing type
    if (isSamePlan && !isSameType) {
      const goingUp = membershipData.type === 'monthly' && targetType === 'yearly';
      // Upgrade (monthly -> yearly): enabled; Downgrade (yearly -> monthly): enabled
      return { label: goingUp ? 'Upgrade' : 'Downgrade', disabled: false };
      //  return { label: goingUp ? "Upgrade" : "Downgrade", disabled: !goingUp };
    }

    // Case 3: Different plan — compare priorities
    const currentPriority = planPriority[membershipData?.planId] ?? (membershipData?.planPrice || 0);
    const targetPriority = planPriority[item?.planId] ?? (targetType === 'monthly' ? item?.monthly_price : item?.yearly_price);

    // If viewing monthly plans while on yearly plan
    if (membershipData.type === 'yearly' && targetType === 'monthly') {
      // Lower tier plans should be downgradable (enabled)
      if (targetPriority < currentPriority) {
        return { label: 'Downgrade', disabled: false };
      }
      // Higher or same tier plans should be upgradable but disabled for monthly
      if (targetPriority >= currentPriority) {
        return { label: 'Downgrade', disabled: true };
      }
    }

    // If viewing yearly plans while on monthly plan
    if (membershipData.type === 'monthly' && targetType === 'yearly') {
      // Any yearly plan is an upgrade from monthly
      if (targetPriority >= currentPriority) {
        return { label: 'Upgrade', disabled: false };
      }
      // Lower tier yearly plans
      if (targetPriority < currentPriority) {
        return { label: 'Downgrade', disabled: false };
      }
    }

    // Same billing type, different plan
    if (isSameType) {
      if (targetPriority > currentPriority) {
        return { label: 'Upgrade', disabled: false };
      }
      if (targetPriority < currentPriority) {
        return { label: 'Downgrade', disabled: false };
      }
    }

    // Same priority different plan — treat as subscribe/switch
    return { label: 'Subscribe', disabled: false };
  };

  const activePlanData = getBillingPlanData?.filter(item => item?.category === activePlan)[0];

  // const uniqueCategories = getBillingPlanData?.map(item => item?.category)?.filter((cat, index, arr) => arr?.indexOf(cat) === index);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        px: { xs: 1.2, sm: 2.5, md: 5 },
        pt: 5
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ ...typographyStyle, fontFamily: 'var(--font-family-rubik)', fontSize: '32px', fontWeight: 600, color: '#000000', letterSpacing: '0.00938em' }}>Pricing</Typography>
        <Typography color="textSecondary" sx={{ ...typographyStyle }}>
          Simple, transparent pricing built for agencies and local businesses.
        </Typography>
      </Box>

      <Tabs
        value={value}
        onChange={(e, val) => {
          setValue(val);
        }}
        variant="scrollable"
        scrollButtons={false}
        aria-label="settings tabs"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          // label="Done For You"
          label="Map Check-Ins"
          sx={{
            fontSize: 15,
            fontFamily: ' var(--font-family-manrope)',
            fontWeight: 500,
            color: '#374151',
            transition: 'color 0.25s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            py: 0.6,
            textTransform: 'capitalize'
          }}
        />
        <Tab
          label="Rank Tracker"
          sx={{
            fontSize: 15,
            fontFamily: ' var(--font-family-manrope)',
            fontWeight: 500,
            color: '#374151',
            transition: 'color 0.25s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            py: 0.6,
            textTransform: 'capitalize'
          }}
        />
      </Tabs>

      {value === 0 && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            {pricingPlans?.map((plan: any, idx: number) => {
              return (
                <Grid key={plan._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <PricingCard title={plan.title} amount={plan.priceLabel} description={plan.description} features={plan.features} buttonLabel={plan.ctaLabel} onButtonClick={() => plan.ctaAction()} />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {value === 1 && (
        <>
          <Box
            sx={{
              mt: 3.5,
              mx: 0
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={5}>
              {/* Monthly */}
              <Typography fontSize={14} fontWeight={billing === 'monthly' ? 600 : 400} color={billing === 'monthly' ? '#111827' : '#6b7280'}>
                Monthly
              </Typography>

              {/* Switch */}
              <Switch
                checked={billing === 'yearly'}
                onChange={() => setBilling(billing === 'monthly' ? 'yearly' : 'monthly')}
                disableRipple
                sx={commonSwitchStyles}
              />

              {/* Yearly */}
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontSize={14} fontWeight={billing === 'yearly' ? 600 : 400} color={billing === 'yearly' ? '#111827' : '#6b7280'}>
                  Yearly
                </Typography>

                {/* Discount Icon */}
                <span
                  style={{
                    color: '#2653EB',
                    margin: '0px 0px 0px 4px',
                    fontSize: '12px',
                    fontFamily: 'var(--font-family-manrope)'
                  }}
                >
                  (2 months free)
                </span>
                {/* <CustomIcons.DiscountMonth /> */}
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {getBillingPlanData
              ?.filter((item: Plan) => {
                if (item.disabled) return false;
                if (billing === 'yearly' && item.yearly === false) return false;
                return true;
              })
              ?.map((plan: Plan, idx) => {
                const { label, disabled } = getPlanButtonState(plan);

                const flattenedFeatures = plan.features?.filter((f: any) => f.subFeatures && f.subFeatures.length > 0 && f.subFeatures.some((sf: string) => sf.trim() !== ''))?.flatMap((f: any) => f.subFeatures.filter((sf: string) => sf.trim() !== '')) || [];

                const isYearlySupported = plan.yearly === true;
                const displayYearly = billing === 'yearly' && isYearlySupported;
                const price = displayYearly ? plan.yearly_price : plan.monthly_price;
                const priceSuffix = displayYearly ? '/yr' : '/mo';

                return (
                  <Grid key={plan._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                    <PricingCard
                      title={plan.name}
                      amount={`$${price}${priceSuffix}`}
                      credits={`${plan.credits} credits / month`}
                      features={flattenedFeatures}
                      buttonLabel={label}
                      onButtonClick={() => {
                        if (label === 'Upgrade') {
                          handleUpgradePreview(plan);
                        }
                        else if (label === 'Downgrade') {
                          handleUpgradePreview(plan);
                        }
                        else {
                          handleBillCheckout(plan, label);
                        }
                      }}
                      buttonDisabled={disabled}
                      recommended={plan._id === recommendedPlanId}
                      mostPopular={idx === 1}
                    />
                  </Grid>
                );
              })}
          </Grid>

          <Box sx={{ pb: 3, pt: 1 }}>
            <Typography sx={{ ...typographyStyle, fontFamily: 'var(--font-family-rubik)', fontSize: '32px', fontWeight: 600, color: '#000000', letterSpacing: '0.00938em', marginTop: '20px' }}>Find the Right Plan</Typography>
            <Typography>Estimate your usage and see which plan fits best.</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ my: 1, width: '200px' }}>
                <Typography sx={{ fontWeight: 500, color: '#000' }}>Number of Keywords</Typography>
                <TextField
                  type="number"
                  className="res-des"
                  size="small"
                  value={NumberOfScan}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 'var(--small-border-radius)',
                      height: '30px',
                      minHeight: '30px',
                      '& input': {
                        padding: '0 12px',
                        height: '30px',
                        lineHeight: '30px'
                      },
                      '& fieldset': {
                        borderRadius: 'var(--small-border-radius)'
                      },
                      '& fieldset legend': {
                        width: 0,
                        maxWidth: 0,
                        padding: 0,
                        visibility: 'hidden'
                      }
                    }
                  }}
                  onChange={e => {
                    const val = e.target.value;

                    if (val === '') {
                      setNumberOfScan(0);
                      return;
                    }

                    if (!/^\d*$/.test(val)) return;

                    const numericVal = Number(val);
                    if (numericVal < 0) return;

                    setNumberOfScan(numericVal);
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={{ my: 1, width: '200px' }}>
                <Typography sx={{ fontWeight: 500, color: '#000' }}>Grid Size</Typography>
                <Select
                  size="small"
                  className="form-select-mui small res-des"
                  value={selectGrid}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        boxShadow: 'none',
                        border: '1px solid rgba(0,0,0,0.06)',
                        minWidth: 240,
                        p: 0.5
                      }
                    },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' }
                  }}
                  onChange={e => setSelectGrid(e.target.value)}
                  renderValue={selected => selected || 'Actions'}
                  sx={{
                    height: '30px',
                    minHeight: '30px',
                    width: '100%',
                    '& .MuiSelect-select': {
                      height: '30px',
                      minHeight: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '0 !important',
                      paddingBottom: '0 !important',
                      paddingLeft: '12px',
                      paddingRight: '32px',
                      fontSize: 'var(--small-body-font-size)',
                      lineHeight: '30px'
                    },

                    '& .MuiInputBase-root': {
                      backgroundColor: '#e3f2fd !important',
                      fontWeight: 400
                    },

                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: 'var(--small-border-radius)'
                    }
                  }}
                >
                  {generatedGridOptions.map(opt => (
                    <MenuItem sx={menuItemSX} key={opt.key} value={opt.label}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ my: 1, width: '200px' }}>
                <Typography sx={{ fontWeight: 500, color: '#000' }}>Scan Frequency</Typography>
                <Select
                  size="small"
                  className="form-select-mui small res-des"
                  value={selectTime}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        boxShadow: 'none',
                        border: '1px solid rgba(0,0,0,0.06)',
                        minWidth: 240,
                        p: 0.5
                      }
                    },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' }
                  }}
                  onChange={e => setSelectTime(e.target.value)}
                  renderValue={selected => selected || selectTime}
                  sx={{
                    height: '30px',
                    minHeight: '30px',
                    minWidth: '200px',
                    '& .MuiSelect-select': {
                      height: '30px',
                      minHeight: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '0 !important',
                      paddingBottom: '0 !important',
                      paddingLeft: '12px',
                      paddingRight: '32px',
                      fontSize: 'var(--small-body-font-size)',
                      lineHeight: '30px'
                    },

                    '& .MuiInputBase-root': {
                      backgroundColor: '#e3f2fd !important',
                      fontWeight: 400
                    },

                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: 'var(--small-border-radius)'
                    }
                  }}
                >
                  {[
                    { key: 'daily', label: 'Daily' },
                    { key: 'weekly', label: 'Weekly' },
                    { key: 'monthly', label: 'Monthly' }
                  ].map(opt => (
                    <MenuItem sx={menuItemSX} key={opt.key} value={opt.label}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box>
              <Typography>
                Estimated credits per month:{' '}
                <Typography component={'span'} sx={{ fontWeight: 600 }}>
                  {estimatedCredits}
                </Typography>
              </Typography>
            </Box>
          </Box>
        </>
      )}

      <UpgradePreviewModal
        open={isUpgradePreviewOpen}
        onClose={() => setIsUpgradePreviewOpen(false)}
        onConfirm={handleConfirmUpgrade}
        loading={loading}
        billing={billing}
      />
    </Box>
  );
}
